import React from "react";
import { Textarea } from "./ui/textarea";

const headingStyle = (tpl) => {
  switch (tpl.heading) {
    case "underline": return { color: "#111827", borderBottom: "1px solid #D1D5DB", paddingBottom: 2 };
    case "caps": return { color: "#6B7280", letterSpacing: "0.14em" };
    case "bar": return { color: tpl.accent, borderLeft: `3px solid ${tpl.accent}`, paddingLeft: 8 };
    default: return { color: tpl.accent };
  }
};

const Heading = ({ tpl, children }) => (
  <h3 className="text-[11px] font-bold uppercase tracking-wider mb-1 mt-3" style={headingStyle(tpl)}>{children}</h3>
);

const Sections = ({ keys, data, tpl, editing, onSummary, onBullet }) => (
  <>
    {keys.map((key) => {
      if (key === "summary" && (data.summary || editing)) {
        return (
          <section key={key} className="mb-3">
            <Heading tpl={tpl}>Summary</Heading>
            {editing
              ? <Textarea value={data.summary || ""} onChange={(e) => onSummary(e.target.value)} className="text-slate-900 min-h-[80px]" data-testid="inline-summary-edit-input" />
              : <p className="text-[13px] leading-relaxed text-slate-700">{data.summary}</p>}
          </section>
        );
      }
      if (key === "skills" && data.skills?.length) {
        return (
          <section key={key} className="mb-3"><Heading tpl={tpl}>Core Skills</Heading>
            <ul className={`text-[13px] text-slate-700 ${tpl.layout === "two" || data.skills.length <= 6 ? "" : "sm:columns-2 sm:gap-6"}`}>
              {data.skills.map((s, si) => (
                <li key={si} className="flex gap-1.5 break-inside-avoid leading-snug">
                  <span className="mt-[7px] h-1 w-1 shrink-0 rounded-full" style={{ background: tpl.accent }} />
                  <span>{s}</span>
                </li>
              ))}
            </ul></section>
        );
      }
      if (key === "experience" && data.experience?.length) {
        return (
          <section key={key} className="mb-3"><Heading tpl={tpl}>Experience</Heading>
            {data.experience.map((e, ei) => (
              <div key={ei} className="mb-2.5">
                <p className="font-semibold text-[13px] text-slate-900">{e.title} — {e.company} <span className="text-slate-500 font-normal">({e.duration})</span></p>
                <ul className="mt-1 space-y-0.5">
                  {e.bullets?.map((b, bi) => (
                    <li key={bi} className="text-[13px] text-slate-700 flex gap-1.5">
                      <span style={{ color: tpl.accent }}>•</span>
                      {editing
                        ? <input value={b} onChange={(ev) => onBullet(ei, bi, ev.target.value)} className="flex-1 border-b border-slate-300 focus:border-slate-400 outline-none text-slate-900" data-testid={`inline-bullet-edit-input-${ei}-${bi}`} />
                        : b}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </section>
        );
      }
      if (key === "projects" && data.projects?.length) {
        return (
          <section key={key} className="mb-3"><Heading tpl={tpl}>Projects</Heading>
            {data.projects.map((p, i) => (<p key={i} className="text-[13px] text-slate-700"><span className="font-semibold">{p.name}:</span> {p.description}</p>))}</section>
        );
      }
      if (key === "education" && data.education?.length) {
        return (
          <section key={key} className="mb-3"><Heading tpl={tpl}>Education</Heading>
            {data.education.map((ed, i) => (<p key={i} className="text-[13px] text-slate-700">{ed}</p>))}</section>
        );
      }
      if (key === "certifications" && data.certifications?.length) {
        return (
          <section key={key} className="mb-3"><Heading tpl={tpl}>Certifications</Heading>
            <ul className="space-y-0.5">{data.certifications.map((c, i) => (<li key={i} className="text-[13px] text-slate-700">• {c}</li>))}</ul></section>
        );
      }
      return null;
    })}
  </>
);

const TemplatedResumePreview = ({ data, tpl, editing, onSummary, onBullet }) => {
  const fontFamily = tpl.serif ? "Georgia, 'Times New Roman', serif" : "'Inter', system-ui, sans-serif";
  const nameAlign = tpl.nameAlign === "center" ? "text-center" : "text-left";
  const props = { data, tpl, editing, onSummary, onBullet };
  return (
    <div className="rounded-xl bg-white text-slate-900 p-5 sm:p-6 md:p-8 shadow-xl" style={{ fontFamily }} data-testid="resume-preview">
      <div className={nameAlign}>
        <h1 className="text-2xl font-extrabold text-slate-900">{data.name || "Candidate"}</h1>
        <p className="font-medium" style={{ color: tpl.accent }}>{data.target_title}</p>
        {!!data.contact?.length && <p className="text-xs text-slate-500 mt-1 break-words" data-testid="resume-contact-details">{data.contact.join(" | ")}</p>}
      </div>
      <hr className="my-3" style={{ borderColor: "#E5E7EB" }} />
      {tpl.layout === "two" ? (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
            <div className="sm:col-span-1 sm:border-r sm:border-slate-200 sm:pr-4">
              <Sections keys={["skills", "education", "certifications"]} {...props} />
            </div>
            <div className="sm:col-span-2">
              <Sections keys={["summary", "experience", "projects"]} {...props} />
            </div>
          </div>
          <p className="sm:hidden mt-4 text-[11px] text-slate-500" data-testid="two-column-mobile-note">Shown stacked for readability on your phone. The download keeps the two-column layout.</p>
        </>
      ) : (
        <Sections keys={["summary", "skills", "experience", "projects", "education", "certifications"]} {...props} />
      )}
    </div>
  );
};

export default TemplatedResumePreview;
